import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@shared/ui/dialog';
import { Button, useToast } from '@shared/ui';
import { useTwoFactor } from '@features/auth/two-factor';

interface TwoFactorModalProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY_CODE = ['', '', '', '', '', ''];

export function TwoFactorModal({ open, onClose }: TwoFactorModalProps) {
  const { t } = useTranslation('auth');
  const { toast } = useToast();
  const { confirmLogin, isLoading } = useTwoFactor();
  const [code, setCode] = useState([...EMPTY_CODE]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isSubmittingRef = useRef(false);

  const isCodeComplete = code.every((d) => d);

  const resetCode = () => {
    setCode([...EMPTY_CODE]);
    inputRefs.current[0]?.focus();
  };

  const submitCode = async (codeString: string) => {
    if (codeString.length !== 6 || isSubmittingRef.current || isLoading) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      const result = await confirmLogin(codeString);

      if (result.success) {
        onClose();
        return;
      }

      if (result.error) {
        toast(result.error, 'error');
        resetCode();
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleSubmit = () => {
    void submitCode(code.join(''));
  };

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((d) => d)) {
      void submitCode(newCode.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.split('');

    while (newCode.length < 6) {
      newCode.push('');
    }

    setCode(newCode);

    if (pastedData.length > 0) {
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }

    if (pastedData.length === 6) {
      void submitCode(pastedData);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('twoFactorAuth')}</DialogTitle>
          <DialogDescription>{t('enterAuthCode')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div>
            <div className="flex gap-2 justify-between" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  className="w-12 h-12 text-center text-lg font-semibold border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground disabled:opacity-50"
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" className="flex-1" disabled={isLoading}>
              {t('back')}
            </Button>
            <Button
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!isCodeComplete}
              className="flex-1"
            >
              {t('verify')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
